import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { GraduationCap, ListChecks, Plus } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectIsOrgOwnerOrAdmin } from "../../features/authentication/authenticationSlice";
import {
  selectMemberEnrolments,
  selectMemberEnrolmentsLoaded,
  selectProgrammes,
  selectProgressByEnrolment,
} from "../../features/training/training-slice";
import MemberProgressPanel from "./MemberProgressPanel";
import TrainingRecordButton from "./TrainingRecordButton";
import EnrolmentSignatureControl from "./EnrolmentSignatureControl";
import EnrolProgrammeForm from "./EnrolProgrammeForm";
import { Skeleton } from "../skeleton/Skeleton";
import {
  fetchMemberEnrolments,
  fetchProgrammes,
  removeEnrolment,
  updateEnrolment,
} from "../../features/training/slice-extensions/training-slice-extension";
import { fetchEnrolmentProgress } from "../../features/training/slice-extensions/training-topics-slice-extension";
import { Enrolment } from "../../features/training/models/Enrolment";
import { ROUTES } from "../../routes";
import { formatDateOnly, todayDateOnly } from "../../features/training/utils/formatDateOnly";
import { getDisplayLocale } from "../../features/utils/timezoneUtils";

interface MemberProgrammesCardProps {
  memberId: string;
}

/**
 * The member's programme enrolments on the member page: enrol in a live
 * programme, mark one completed or re-open it, and (Owner/Admin) remove it.
 */
const MemberProgrammesCard: React.FC<MemberProgrammesCardProps> = ({ memberId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const canManage = useAppSelector(selectIsOrgOwnerOrAdmin);
  const enrolments = useAppSelector(selectMemberEnrolments(memberId));
  const loaded = useAppSelector(selectMemberEnrolmentsLoaded(memberId));
  const [loadFailed, setLoadFailed] = useState(false);
  const programmes = useAppSelector(selectProgrammes);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const progressByEnrolment = useAppSelector(selectProgressByEnrolment);

  const loadEnrolments = useCallback(() => {
    setLoadFailed(false);
    dispatch(fetchMemberEnrolments(memberId)).then((result) => {
      if (fetchMemberEnrolments.rejected.match(result)) setLoadFailed(true);
    });
  }, [dispatch, memberId]);

  useEffect(() => {
    loadEnrolments();
    dispatch(fetchProgrammes());
  }, [dispatch, loadEnrolments]);

  // A second enrol in the same programme is refused by the server (409) and explained below.
  const choices = useMemo(() => programmes.filter((p) => !p.isArchived), [programmes]);

  const toggleProgress = (enrolment: Enrolment) => {
    if (expandedId === enrolment.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(enrolment.id);
    dispatch(fetchEnrolmentProgress({ memberId, enrolmentId: enrolment.id }));
  };

  const setCompleted = (enrolment: Enrolment, completedOn: string | null) => {
    dispatch(updateEnrolment({ memberId, enrolmentId: enrolment.id, request: { completedOn } }));
  };

  const date = (value: string): string => formatDateOnly(value, getDisplayLocale());

  return (
    <div className="with-border rounded-2xl p-4 lg:p-6">
      <div className="mb-4 flex items-center gap-2">
        <GraduationCap size={20} className="text-primary-40 dark:text-primary-90" />
        <h3 className="text-grey-10 dark:text-grey-90">{t("training.enrolments.card")}</h3>
      </div>

      {/* Until the enrolments arrive the card shows a placeholder, never a premature "none yet". */}
      {!loaded && !loadFailed ? (
        <div className="space-y-2" role="status" aria-label={t("common.loading")}>
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      ) : loadFailed ? (
        <p className="text-sm text-grey-30 dark:text-grey-70" role="alert">
          {t("training.enrolments.loadFailed")}{" "}
          <button type="button" className="font-medium text-primary-40 dark:text-primary-90 hover:underline" onClick={loadEnrolments}>
            {t("common.retry")}
          </button>
        </p>
      ) : enrolments.length === 0 ? (
        <p className="text-sm text-grey-30 dark:text-grey-70">{t("training.enrolments.empty")}</p>
      ) : (
        <ul className="space-y-3">
          {enrolments.map((enrolment) => (
            <li key={enrolment.id} className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-medium text-grey-10 dark:text-grey-90">{enrolment.programmeName}</p>
                <p className="text-sm text-grey-50 dark:text-grey-70">
                  {enrolment.completedOn
                    ? t("training.enrolments.completedOn", { date: date(enrolment.completedOn) })
                    : t("training.enrolments.enrolledOn", { date: date(enrolment.enrolledOn) })}
                </p>
                <button
                  type="button"
                  aria-expanded={expandedId === enrolment.id}
                  className="mt-1 inline-flex items-center gap-1 text-sm text-primary-40 dark:text-primary-90 hover:underline"
                  onClick={() => toggleProgress(enrolment)}
                >
                  <ListChecks size={16} />
                  <span>
                    {/* "Ausbildungsstand" is driving-school wording; a course plan reads neutrally (PD-0023). */}
                    {enrolment.isLicenceClassProgramme
                      ? t(expandedId === enrolment.id ? "training.progress.hide" : "training.progress.show")
                      : t(expandedId === enrolment.id ? "training.progress.hideNeutral" : "training.progress.showNeutral")}
                  </span>
                </button>
                {enrolment.isLicenceClassProgramme && (
                  <>
                    <TrainingRecordButton memberId={memberId} enrolmentId={enrolment.id} />
                    <EnrolmentSignatureControl memberId={memberId} enrolment={enrolment} />
                  </>
                )}
                {expandedId === enrolment.id && progressByEnrolment[enrolment.id] && (
                  <MemberProgressPanel progress={progressByEnrolment[enrolment.id]} />
                )}
              </div>
              <div className="flex items-center gap-3 text-sm">
                <button
                  type="button"
                  className="text-primary-40 dark:text-primary-90 hover:underline"
                  onClick={() => setCompleted(enrolment, enrolment.completedOn ? null : todayDateOnly())}
                >
                  {enrolment.completedOn
                    ? t("training.enrolments.reopen")
                    : t("training.enrolments.markCompleted")}
                </button>
                {canManage && (
                  <button
                    type="button"
                    className="text-grey-50 dark:text-grey-70 hover:underline"
                    onClick={() => dispatch(removeEnrolment({ memberId, enrolmentId: enrolment.id }))}
                  >
                    {t("training.enrolments.remove")}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {programmes.length === 0 ? (
        canManage ? (
          <Link to={ROUTES.PROGRAMMES_SETTINGS} className="btn btn-text mt-4">
            <Plus size={16} />
            <span>{t("training.enrolments.createProgramme")}</span>
          </Link>
        ) : (
          <p className="mt-4 text-sm text-grey-50 dark:text-grey-70">{t("training.enrolments.noProgrammes")}</p>
        )
      ) : (
        <EnrolProgrammeForm memberId={memberId} programmes={choices} />
      )}
    </div>
  );
};

export default MemberProgrammesCard;
